import { useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
};

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "?";

// iMessage-style "who reacted" sheet: emoji filter chips + list of people with their reaction.
export const WhoReactedSheet = ({ open, reactions = [], meType, meId, onRemoveMine, onClose, isLight }) => {
  const [filter, setFilter] = useState("all");
  const counts = reactions.reduce((acc, r) => ((acc[r.emoji] = (acc[r.emoji] || 0) + 1), acc), {});
  const list = (filter === "all" ? reactions : reactions.filter((r) => r.emoji === filter)).slice().sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const isMe = (r) => r.by_type === meType && r.by_id === meId;

  const sheet = isLight ? "bg-white text-gray-900" : "bg-[#1c1c1e] text-white";
  const sub = isLight ? "text-gray-500" : "text-white/50";
  const chip = (on) => `px-3 h-8 rounded-full text-sm font-medium flex items-center gap-1 ${on ? "bg-[#0A84FF] text-white" : isLight ? "bg-gray-100 text-gray-700" : "bg-white/10 text-white/80"}`;
  const row = isLight ? "border-gray-100" : "border-white/10";

  return ReactDOM.createPortal(
    <AnimatePresence>
      {open && (
        <motion.div key="who" className="fixed inset-0 z-[10001]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} data-testid="who-reacted-sheet">
          <button className="absolute inset-0 bg-black/30" onClick={onClose} aria-label="Close" data-testid="who-reacted-backdrop" />
          <motion.div
            className={`absolute left-0 right-0 bottom-0 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md sm:bottom-6 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[60vh] flex flex-col ${sheet}`}
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 420, damping: 36 }}
          >
            <div className="mx-auto mt-2 w-10 h-1.5 rounded-full bg-gray-400/40" />
            <div className="flex items-center gap-2 px-4 pt-3 pb-2 overflow-x-auto">
              <button onClick={() => setFilter("all")} className={chip(filter === "all")} data-testid="who-filter-all">All <span className="opacity-70">{reactions.length}</span></button>
              {Object.entries(counts).map(([emoji, n]) => (
                <button key={emoji} onClick={() => setFilter(emoji)} className={chip(filter === emoji)} data-testid={`who-filter-${emoji}`}>
                  <span className="text-base leading-none">{emoji}</span><span className="opacity-70">{n}</span>
                </button>
              ))}
            </div>
            <div className="overflow-y-auto px-4 pb-3">
              {list.map((r) => {
                const me = isMe(r);
                return (
                  <div key={`${r.by_type}-${r.by_id}-${r.emoji}`} className={`flex items-center gap-3 py-2.5 border-b last:border-b-0 ${row}`} data-testid="who-reacted-row">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${me ? "bg-[#0A84FF] text-white" : "bg-gradient-to-br from-[#00D4FF] to-[#8B5CF6] text-white"}`}>{initials(r.by_name)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[15px] truncate">{me ? "You" : r.by_name}</p>
                      <p className={`text-xs ${sub}`}>{me ? "Tap emoji to remove" : ago(r.at)}</p>
                    </div>
                    <button
                      onClick={() => me && onRemoveMine(r.emoji)}
                      disabled={!me}
                      className={`text-[28px] leading-none w-11 h-11 rounded-full flex items-center justify-center ${me ? "active:scale-90 transition-transform" : ""}`}
                      aria-label={me ? "Remove my reaction" : r.emoji}
                      data-testid={me ? "who-remove-mine" : "who-reaction-emoji"}
                    >
                      {r.emoji}
                    </button>
                  </div>
                );
              })}
              {list.length === 0 && <p className={`text-sm py-6 text-center ${sub}`}>No reactions</p>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
